/**
 * Footer - compact BeClass Manager branding for dashboard shells.
 * Public microsite pages use PublicFooter.
 *
 * @example
 * ```tsx
 * <Footer />
 * <Footer fullWidth />
 * ```
 */

"use client";

import { Divider, Flex } from "@chakra-ui/react";
import { AppLogo } from "@/components";
import { Card, Link, Text } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";

interface FooterProps {
  /**
   * Whether footer should match dashboard content width (for dashboard layout)
   */
  fullWidth?: boolean;
}

export const Footer = ({ fullWidth = false }: FooterProps = {}) => {
  const theme = useThemeTokens();

  return (
    <Flex
      w="100%"
      mx="auto"
      zIndex={fullWidth ? "auto" : 1.5}
      justify="center"
      px={fullWidth ? { base: 4, lg: 8 } : 0}
      position="relative"
    >
      <Card
        w={fullWidth ? "100%" : "1044px"}
        maxW={fullWidth ? "100%" : "calc(100% - 2rem)"}
        mx={fullWidth ? 0 : "auto"}
        mb={fullWidth ? "30px" : "20px"}
        p={4}
        borderColor={theme.borderGlass.default}
        borderWidth="1px"
        background={"none"}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "center", md: "center" }}
          justify="space-between"
          gap={{ base: 4, md: 6 }}
        >
          {/* BeClass Manager Logo */}
          <Link href="https://beclassmanager.com" external>
            <AppLogo />
          </Link>

          {/* Divider - Only on mobile */}
          <Divider
            display={{ base: "block", md: "none" }}
            borderColor={theme.borderGlass.default}
            opacity={0.3}
          />

          {/* Copyright Text */}
          <Text
            variant="tertiary"
            textAlign={{ base: "center", md: "right" }}
            fontSize="sm"
            fontWeight="400"
          >
            &copy; {new Date().getFullYear()}{" "}
            <Link
              href="https://beclassmanager.com"
              external
              color={theme.accent.primary}
              fontWeight="500"
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    textDecoration: "underline",
                  },
                },
              }}
            >
              BeClass Manager
            </Link>
            . All Rights Reserved.
          </Text>
        </Flex>
      </Card>
    </Flex>
  );
};
